import React from 'react';
import {JobApplicationStatus} from '../../../models/job-application';
import ProfilePicture from '../../../components/profile-picture';
import {Tag, TagSize, TagVariant} from '../../../components/tag';
import {ApplicantStatusBadge} from './applicant-status-badge';
import {AuraScoreBar} from './aura-score-bar';
import {RecommendedCandidateEntry} from '../../../services/opportunity';

const CARD_BASE_CLASS =
  'relative w-full min-w-[200px] max-w-xs p-4 rounded-xl shadow-sm flex flex-col items-center gap-2 border border-gray-200 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5';

const VISIBLE_SKILLS_MAX = 4;

function getRankLabel(rank: number): string {
  if (rank === 1) return 'Top Match';
  if (rank === 2) return 'Strong Fit';
  if (rank === 3) return 'Good Fit';
  return `#${rank}`;
}

function getRankBadgeClass(rank: number): string {
  if (rank === 1) return 'bg-amber-500/90 text-white border-amber-400 shadow-sm';
  if (rank === 2) return 'bg-gray-400/90 text-white border-gray-300 shadow-sm';
  if (rank === 3) return 'bg-amber-700/80 text-white border-amber-600 shadow-sm';
  return 'bg-gray-700 text-white border-gray-600';
}

function isRequiredSkill(skillLabel: string, requiredSkillNames: string[]): boolean {
  const normalized = skillLabel.trim().toLowerCase();
  return requiredSkillNames.some(
    r => r.trim().toLowerCase() === normalized,
  );
}

/** Puts matched (required) skills first so they are visible and highlighted. */
function visibleSkillsWithMatchesFirst<T extends {label: string}>(
  allSkills: T[],
  requiredSkillNames: string[],
  maxVisible: number,
): {remainingCount: number; visible: T[]} {
  if (requiredSkillNames.length === 0) {
    const visible = allSkills.slice(0, maxVisible);
    return {remainingCount: Math.max(0, allSkills.length - maxVisible), visible};
  }
  const matched: T[] = [];
  const rest: T[] = [];
  for (const s of allSkills) {
    if (isRequiredSkill(s.label, requiredSkillNames)) matched.push(s);
    else rest.push(s);
  }
  const visible = [...matched, ...rest].slice(0, maxVisible);
  const remainingCount = Math.max(0, allSkills.length - maxVisible);
  return {remainingCount, visible};
}

export interface RecommendedCandidateCardProps {
  entry: RecommendedCandidateEntry;
  rank?: number;
  requiredSkillNames?: string[];
  inviteError?: string;
  isInvited?: boolean;
  isInviting?: boolean;
  onInvite?: () => void;
  onReview?: () => void;
  onViewProfile?: () => void;
}

export const RecommendedCandidateCard: React.FC<RecommendedCandidateCardProps> = ({
  entry,
  rank,
  requiredSkillNames = [],
  inviteError,
  isInvited,
  isInviting,
  onInvite,
  onReview,
  onViewProfile,
}) => {
  const {application, candidate, inviteEligible} = entry;
  const name = candidate.fullName || 'Candidate';
  const scorePercent = typeof candidate.score === 'number' ? Math.round(candidate.score * 100) : 0;
  const headline = candidate.headline;
  const location = candidate.location;
  const allSkills = candidate.skills ?? [];
  const {remainingCount, visible: visibleSkills} = visibleSkillsWithMatchesFirst(
    allSkills,
    requiredSkillNames,
    VISIBLE_SKILLS_MAX,
  );
  const cardClass = `${CARD_BASE_CLASS} ${isInvited ? 'bg-gray-50 opacity-95' : 'bg-white'}`;

  const primaryButtonClass =
    'w-full px-3 py-2 text-white text-sm font-medium rounded-lg transition bg-blue-600 hover:bg-blue-700';
  const secondaryButtonClass =
    'w-full px-3 py-2 text-sm font-medium rounded-lg transition border border-gray-300 bg-white text-gray-700 hover:bg-gray-50';

  if (inviteEligible) {
    return (
      <div className={cardClass}>
        {rank != null && (
          <div className="absolute top-2.5 left-2.5 flex flex-col gap-0.5">
            <span
              className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border px-1.5 text-[10px] font-bold ${getRankBadgeClass(rank)}`}>
              #{rank}
            </span>
            {(rank === 1 || rank === 2 || rank === 3) && (
              <span className="text-[10px] font-medium text-gray-500 leading-tight">
                {getRankLabel(rank)}
              </span>
            )}
          </div>
        )}
        {isInvited && (
          <div className="absolute top-2.5 right-2.5">
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800">
              Invited
            </span>
          </div>
        )}
        <div className="inline-flex rounded-full overflow-hidden scale-90">
          <ProfilePicture name={name} url={candidate.profileImageUrl} />
        </div>
        <div className="flex flex-col items-center gap-0 text-center w-full">
          <h3 className="text-gray-900 font-semibold text-sm">{name}</h3>
          {headline && (
            <p className="text-gray-600 text-xs line-clamp-1 w-full" title={headline}>
              {headline}
            </p>
          )}
          {location && (
            <p className="text-gray-500 text-[11px]">{location}</p>
          )}
        </div>
        {visibleSkills.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-center w-full">
            {visibleSkills.map((s, i) => {
              const required = isRequiredSkill(s.label, requiredSkillNames);
              return (
                <Tag
                  key={s.id ?? i}
                  variant={required ? TagVariant.Primary : TagVariant.Default}
                  size={TagSize.SM}
                  className="text-[10px] px-1.5 py-0.5">
                  {s.label}
                </Tag>
              );
            })}
            {remainingCount > 0 && (
              <span className="text-gray-400 text-[10px] px-1.5 py-0.5">+{remainingCount}</span>
            )}
          </div>
        )}
        <AuraScoreBar
          auraScore={scorePercent}
          requiredSkillsMet={requiredSkillNames.length > 0 ? visibleSkills.filter(s => isRequiredSkill(s.label, requiredSkillNames)).length : undefined}
          requiredSkillsTotal={requiredSkillNames.length > 0 ? requiredSkillNames.length : undefined}
          missingSkillNames={
            requiredSkillNames.length > 0
              ? requiredSkillNames.filter(
                  r => !visibleSkills.some(s => isRequiredSkill(s.label, [r])),
                )
              : undefined
          }
        />
        <div className="w-full flex flex-col gap-1.5">
          {onInvite != null && (
            <div className="text-center">
              {inviteError && (
                <p className="text-red-600 text-[10px] mb-0.5" role="alert">
                  {inviteError}
                </p>
              )}
              {isInvited || isInviting ? (
                <span className="text-gray-500 text-xs">{isInviting ? 'Sending…' : 'Invited'}</span>
              ) : (
                <button onClick={onInvite} className={primaryButtonClass}>
                  Invite
                </button>
              )}
            </div>
          )}
          {onViewProfile && (
            <button onClick={onViewProfile} className={secondaryButtonClass}>
              View Profile
            </button>
          )}
        </div>
      </div>
    );
  }

  const status = application?.status ?? JobApplicationStatus.APPLIED;
  const reviewLabel =
    status === JobApplicationStatus.APPLIED
      ? 'Shortlist from suggestions'
      : status === JobApplicationStatus.ACCEPTED
        ? 'Shortlisted'
        : status === JobApplicationStatus.REJECTED
          ? 'View Feedback'
          : 'Review';

  return (
    <div className={cardClass}>
      {rank != null && (
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-0.5">
          <span
            className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border px-1.5 text-[10px] font-bold ${getRankBadgeClass(rank)}`}>
            #{rank}
          </span>
          {(rank === 1 || rank === 2 || rank === 3) && (
            <span className="text-[10px] font-medium text-gray-500 leading-tight">
              {getRankLabel(rank)}
            </span>
          )}
        </div>
      )}
      {isInvited && (
        <div className="absolute top-2.5 right-2.5">
          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800">
            Invited
          </span>
        </div>
      )}
      <ApplicantStatusBadge status={status} />
      <div className="inline-flex rounded-full overflow-hidden scale-90">
        <ProfilePicture name={name} url={candidate.profileImageUrl} />
      </div>
      <div className="flex flex-col items-center gap-0 text-center w-full">
        <h3 className="text-gray-900 font-semibold text-sm">{name}</h3>
        {headline && (
          <p className="text-gray-600 text-xs line-clamp-1 w-full" title={headline}>
            {headline}
          </p>
        )}
        {location && (
          <p className="text-gray-500 text-[11px]">{location}</p>
        )}
      </div>
      {visibleSkills.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center w-full">
          {visibleSkills.map((s, i) => {
            const required = isRequiredSkill(s.label, requiredSkillNames);
            return (
              <Tag
                key={s.id ?? i}
                variant={required ? TagVariant.Primary : TagVariant.Default}
                size={TagSize.SM}
                className="text-[10px] px-1.5 py-0.5">
                {s.label}
              </Tag>
            );
          })}
          {remainingCount > 0 && (
            <span className="text-gray-400 text-[10px] px-1.5 py-0.5">+{remainingCount}</span>
          )}
        </div>
      )}
      <AuraScoreBar
        auraScore={scorePercent}
        requiredSkillsMet={requiredSkillNames.length > 0 ? visibleSkills.filter(s => isRequiredSkill(s.label, requiredSkillNames)).length : undefined}
        requiredSkillsTotal={requiredSkillNames.length > 0 ? requiredSkillNames.length : undefined}
        missingSkillNames={
          requiredSkillNames.length > 0
            ? requiredSkillNames.filter(
                r => !visibleSkills.some(s => isRequiredSkill(s.label, [r])),
              )
            : undefined
        }
      />
      <div className="w-full flex flex-col gap-1.5">
        {onReview && (
          <button onClick={onReview} className={primaryButtonClass}>
            {reviewLabel}
          </button>
        )}
        {onViewProfile && (
          <button onClick={onViewProfile} className={secondaryButtonClass}>
            View Profile
          </button>
        )}
      </div>
    </div>
  );
};
